import { Link } from "react-router-dom";
import { Mail, User, CreditCard, ShieldAlert, Wrench } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";

const TOPICS = [
  {
    icon: User,
    title: "Account help",
    body:
      "Trouble signing in, resetting your password, or updating your profile? Use “Forgot password” on the sign-in screen first. If you're still stuck, email us from the address on your account and include your handle.",
  },
  {
    icon: CreditCard,
    title: "Purchases & billing",
    body:
      "Questions about Panda Coins, subscriptions, or a charge you don't recognize? Include the date of purchase and the receipt (Apple email receipt for in-app purchases, or your card statement line for web purchases) so we can locate the transaction quickly.",
  },
  {
    icon: ShieldAlert,
    title: "Safety & reporting",
    body:
      "To report content or a member, use the Report option available on every post, stream, and profile — reports go straight to our moderation team. For urgent safety concerns, email us with “SAFETY” in the subject line and we'll prioritize it.",
  },
  {
    icon: Wrench,
    title: "Technical issues",
    body:
      "App crashing, videos not loading, or something looks broken? Tell us your device model, OS version, what you tapped, and what happened. Screenshots or screen recordings help us fix things fast.",
  },
];

export default function SupportPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-rose-400"
            data-testid="support-home-link"
          >
            ← My Panda X
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            Adults 18+ only
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1
          className="text-3xl sm:text-4xl lg:text-5xl font-serif mb-4"
          data-testid="support-heading"
        >
          MyPandaX Support
        </h1>
        <p className="text-sm text-zinc-400 mb-10 max-w-2xl">
          We're here to help. Check the topics below, then reach out — a real
          human reads every message.
        </p>

        <div className="grid sm:grid-cols-2 gap-4 mb-12">
          {TOPICS.map((t) => (
            <div
              key={t.title}
              className="border border-white/10 bg-white/[0.02] p-5"
              data-testid={`support-topic-${t.title.split(" ")[0].toLowerCase()}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <t.icon className="w-4 h-4 text-rose-400" />
                <h2 className="text-base md:text-lg font-semibold">{t.title}</h2>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>

        <div
          className="border border-rose-600/40 bg-rose-600/5 p-6 mb-12"
          data-testid="support-contact-card"
        >
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-rose-400" />
            <h2 className="text-base md:text-lg font-semibold">Contact us</h2>
          </div>
          <p className="text-sm text-zinc-300">
            Email{" "}
            <a
              href="mailto:support@mypandax.com"
              className="text-rose-400 underline underline-offset-2"
              data-testid="support-email-link"
            >
              support@mypandax.com
            </a>
          </p>
          <p className="text-sm text-zinc-400 mt-1" data-testid="support-response-time">
            Expected response time: 1–2 business days.
          </p>
        </div>

        <h2 className="text-base md:text-lg font-semibold mb-3">Policies</h2>
        <ul className="space-y-2 text-sm mb-4">
          <li>
            <Link
              to="/legal/privacy-policy"
              className="text-zinc-300 hover:text-rose-400 underline underline-offset-2"
              data-testid="support-link-privacy"
            >
              Privacy Policy
            </Link>
          </li>
          <li>
            <Link
              to="/legal/terms-of-service"
              className="text-zinc-300 hover:text-rose-400 underline underline-offset-2"
              data-testid="support-link-terms"
            >
              Terms of Service
            </Link>
          </li>
          <li>
            <Link
              to="/legal/refund-and-cancellation-policy"
              className="text-zinc-300 hover:text-rose-400 underline underline-offset-2"
              data-testid="support-link-refund"
            >
              Refund &amp; Cancellation Policy
            </Link>
          </li>
        </ul>
      </main>

      <SiteFooter />
    </div>
  );
}
